import { marked } from 'marked'
import hljs from 'highlight.js'
import katex from 'katex'

// Configure marked with custom renderer
const renderer = new marked.Renderer()

// Custom link renderer to open links in new tab
renderer.link = function({ href, title, text }) {
  const titleAttr = title ? ` title="${title}"` : ''
  return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
}

// Custom code block renderer with syntax highlighting
renderer.code = function({ text, lang, escaped }) {
  const codeText = text || ''
  const language = lang || ''

  if (language && hljs.getLanguage(language)) {
    try {
      const highlighted = hljs.highlight(codeText, { language }).value
      return `<pre><code class="hljs language-${language}">${highlighted}</code></pre>`
    } catch (err) {
      console.error('Highlight error:', err)
    }
  }

  // Fallback to auto-detection
  try {
    const highlighted = hljs.highlightAuto(codeText).value
    return `<pre><code class="hljs">${highlighted}</code></pre>`
  } catch (err) {
    // Escape HTML entities if not already escaped
    const escaped = codeText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return `<pre><code>${escaped}</code></pre>`
  }
}

// Custom inline code renderer
renderer.codespan = function({ text }) {
  const codeText = text || ''
  const escaped = codeText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return `<code class="inline-code">${escaped}</code>`
}

marked.setOptions({
  renderer: renderer,
  breaks: true,
  gfm: true
})

export function renderMarkdown(text) {
  if (!text) return ''

  try {
    const mathPlaceholders = new Map()
    let placeholderIndex = 0

    // First handle display math before markdown processing
    let processed = text.replace(/\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$/g, (match, bracketMath, dollarMath) => {
      try {
        const math = bracketMath ?? dollarMath
        const rendered = katex.renderToString(math, { throwOnError: false, displayMode: true })
        const placeholder = `###MATHPLACEHOLDER${placeholderIndex}###`
        mathPlaceholders.set(placeholder, rendered)
        placeholderIndex++
        return placeholder
      } catch (err) {
        console.error('KaTeX display math error:', err)
        return match
      }
    })

    // Then handle inline math
    processed = processed.replace(/\\\(([\s\S]+?)\\\)|\$([^\$\n]+?)\$/g, (match, parenMath, dollarMath) => {
      try {
        const math = parenMath ?? dollarMath
        const rendered = katex.renderToString(math, { throwOnError: false, displayMode: false })
        const placeholder = `###MATHPLACEHOLDER${placeholderIndex}###`
        mathPlaceholders.set(placeholder, rendered)
        placeholderIndex++
        return placeholder
      } catch (err) {
        console.error('KaTeX inline math error:', err)
        return match
      }
    })

    // Process markdown
    let html = marked.parse(processed)

    // Restore math placeholders
    mathPlaceholders.forEach((rendered, placeholder) => {
      html = html.split(placeholder).join(rendered)
    })

    return html
  } catch (err) {
    console.error('Markdown rendering error:', err, err.stack)
    // Fallback to plain text with basic HTML escaping
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>')
  }
}
